export type __AttachmentType = {
    id: number;
    file: string;
    alt?: string;
    type?: __AttachmentTypeEnum;
    usage?: __AttachmentUsageEnum;
};

export type __ProfileType<e = 'list' | 'detail'> = {
    id: number;
    first_name?: string;
    last_name?: string;
    phone: e extends 'detail' ? string : never;
    email?: e extends 'detail' ? string : never;
    avatar?: __AttachmentType;
    national_code?: e extends 'detail' ? number : never;
};

export type __LocationType<e = 'list' | 'detail'> = {
    id: number;
    title: string;
    is_selectable: boolean;
    coefficient?: number;
    parent?: { id: number; title: string };
    longitude?: e extends 'detail' ? number : never;
    latitude?: e extends 'detail' ? number : never;
    type: e extends 'detail' ? __LocationTypeEnum : never;
};

export type __CategoryType<e = 'list' | 'detail'> = {
    id: number;
    slug: string;
    title: string;
    price: number;
    renewed_price: number;
    image?: __AttachmentType;
    icon?: string;
    volume?: __CategoryVolumeEnum;
    parent?: e extends 'detail' ? __CategoryType<'list'> : number;
    description?: e extends 'detail' ? string : never;
    order?: number;
};

export enum __CategoryVolumeEnum {
    all = 1,
    single = 2,
    multi = 3,
}

export type __CategoryHierarchyType = __CategoryType<'list'> & { children?: __CategoryHierarchyType[] };

export type __CategoryFieldType<e = 'list' | 'detail'> = {
    id: number;
    title: string;
    category: e extends 'detail' ? __CategoryType<'list'> : never;
    type: __CategoryFieldTypeEnum;
    unit?: string;
    is_filter?: boolean;
    is_label?: boolean;
    required?: boolean;
    field_options?: e extends 'detail' ? __FieldOptionType<'list'>[] : never;
    setting?: { [k: string]: number | string };
    order?: number;
};

export type __FieldValueType<e = 'list' | 'detail'> = {
    id: number;
    category_field: e extends 'detail' ? __CategoryFieldType<'list'> : number;
    value?: string;
    value_number?: number;
    option?: e extends 'detail' ? __FieldOptionType<'list'> : number;
    post?: number;
};

export type __FieldOptionType<e = 'list' | 'detail'> = {
    id: number;
    title: string;
    category_field: e extends 'detail' ? __CategoryFieldType<'list'> : never;
};

export type __PostType<e = 'list' | 'detail'> = {
    id: number;
    slug: string;
    title: string;
    views_count: number;
    likes_count: number;
    has_phone: boolean;
    created_at: string;
    renewed_at?: string;
    main_image?: __AttachmentType;
    banner?: __AttachmentType;
    payment_status?: __PaymentStatusEnum;
    locations?: __LocationType<'list'>[];
    like_id?: e extends 'detail' ? number : never;
    bookmark_id?: e extends 'detail' ? number : never;
    is_report: e extends 'detail' ? boolean : never;
    owner: e extends 'detail' ? __ProfileType<'list'> : never;
    category_fields?: e extends 'detail' ? string : never;
    category: e extends 'detail' ? __CategoryType<'list'> : never;
    category_fields_values?: e extends 'detail' ? __FieldValueType<'detail'>[] : never;
    gallery?: __AttachmentType[];
    approval_status: e extends 'detail' ? __ApprovalStatusEnum : never;
    changed_at?: e extends 'detail' ? string : never;
    reject_reason?: e extends 'detail' ? string : never;
    is_active?: e extends 'detail' ? boolean : never;
    description?: e extends 'detail' ? string : never;
    address?: e extends 'detail' ? string : never;
    latitude?: e extends 'detail' ? number : never;
    longitude?: e extends 'detail' ? number : never;
    republished_at?: e extends 'detail' ? string : never;
    phone?: e extends 'detail' ? string : never;
    chatable?: e extends 'detail' ? boolean : never;
    socials?: e extends 'detail' ? { [key: string]: string } : never;
    is_owner?: boolean;
    labeled_category_field_values?: e extends 'list' ? __FieldValueType<'detail'>[] : never;
    my_chat?: e extends 'detail' ? __ChatType : never;
    is_renewed?: boolean;
};

export type __BookmarkType = {
    id: number;
    created_at: string;
    post: number;
};

export type __ViewType = {
    id: number;
    created_at: string;
    post: number;
};

export type __ChatType = {
    id: number;
    user: __ProfileType<'list'>;
    post: __PostType<'list'>;
    last_message?: __MessageType;
    unread_messages?: number;
    created_at: string;
    is_reported?: "I've reported" | "I'm reported";
};

export type __MessageType = {
    id: number;
    created_at: string;
    text: string;
    status: __MessageStatusType;
    chat: __ChatType;
    user: __ProfileType<'list'>;
    file: __AttachmentType[];
};

export type __SettingType = {
    id: number;
    key: string;
    value: string;
};

export type __TicketType = {
    id: number;
    created_at: string;
    title: string;
    priority: __TicketPriorityEnum;
    status: __TicketStatusEnum;
    category: __TicketCategoryEnum;
    user: __ProfileType<'list'>;
};

export enum __TicketCategoryEnum {
    suggestion = 1,
    postIssues = 2,
    bug = 3,
    finance = 4,
    categories = 5,
    other = 6,
}

export type __TicketMessageType = {
    id: number;
    created_at: string;
    text: string;
    status: 1;
    ticket: __TicketType;
    user: __ProfileType<'list'>;
    file: __AttachmentType[];
};

export type __PostLocationType = {
    address?: string;
    latitude?: number;
    longitude?: number;
    location?: {
        id: number;
        title: string;
    };
};

export enum __TicketPriorityEnum {
    high = 1,
    medium,
    low,
}

export enum __TicketStatusEnum {
    answered = 1,
    pending,
    closed,
}

export enum __AttachmentTypeEnum {
    image = 1,
    document,
    video,
    audio,
    archive,
    qrcode,
}

export enum __AttachmentUsageEnum {
    content = 1,
    avatar,
    document,
}

export enum __LocationTypeEnum {
    province = 1,
    region,
    city,
}

export enum __CategoryFieldTypeEnum {
    number = 1,
    boolean,
    fieldOptions,
    text,
}

export enum __ApprovalStatusEnum {
    approved = 'approved',
    pending = 'pending',
    rejected = 'rejected',
}

export enum __MessageStatusType {
    unread = 1,
    read,
}

export enum __ReportProblemEnum {
    fraud = 1,
    illegalOrUnethical,
    wrongCategory,
    wrongPrice,
    wrongInformation,
    duplicateOrSpam,
    unavailableOrUnresponsive,
    other,
}

export enum __PaymentStatusEnum {
    pending = 1,
    success,
    failed,
}

export enum __PaymentTypeEnum {
    create = 1,
    renew,
}
